/**
 * Infrastructure Compare Bar Controller
 * Handles persistent compare tray across category listings and compare pages.
 */

const STORAGE_KEY = 'cpf_compare_products';
const MAX_COMPARE = 4;

export function getStoredCompare() {
    try {
        return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    } catch (e) {
        return [];
    }
}

export function setStoredCompare(items) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
        // ignore storage errors
    }
}

export default function InfrastructureCompareBar(el, params = {}) {
    const compareItemsCont = el.querySelector('#infrastructure-compare-items');
    const compareCountEl = el.querySelector('#infrastructure-compare-count');
    const compareLink = el.querySelector('#infrastructure-compare-link');
    const compareClearBtn = el.querySelector('#infrastructure-compare-clear');
    const currentCategorySlug = params.categorySlug || '';

    function syncCompareCheckboxes() {
        const stored = getStoredCompare();
        const storedIds = new Set(stored.map(p => String(p.id)));

        document.querySelectorAll('input[data-compare-checkbox]').forEach(cb => {
            const pid = String(cb.getAttribute('data-product-id'));
            cb.checked = storedIds.has(pid);
        });
    }

    function removeCompareProduct(productId) {
        const stored = getStoredCompare().filter(p => String(p.id) !== String(productId));
        setStoredCompare(stored);
        updateCompareBar(); // eslint-disable-line no-use-before-define
    }

    function updateCompareBar() {
        const stored = getStoredCompare();
        const count = stored.length;
        const storedCat = count > 0 ? (stored[0].category || '') : '';

        // Only show if:
        // 1. At least 1 item is stored
        // 2. We are on a category page (currentCategorySlug is not empty)
        // 3. Stored category matches the current active category
        const shouldShow = count > 0 && currentCategorySlug !== ''
            && (!storedCat || storedCat === currentCategorySlug);

        el.style.display = shouldShow ? 'block' : 'none';

        if (compareCountEl) {
            compareCountEl.textContent = count;
        }

        if (compareLink) {
            const slugs = stored.map(p => p.slug || p.title).join(',');
            compareLink.href = `/infrastructure/compare?items=${encodeURIComponent(slugs)}`;
        }

        if (compareItemsCont) {
            compareItemsCont.innerHTML = stored.map(p => `
                <div class="infrastructure-compare-chip" data-chip-id="${p.id}">
                    <button type="button" class="chip-remove-btn"
                        data-remove-id="${p.id}" aria-label="Remove ${p.title}">×</button>
                    <div class="chip-media">
                        ${p.image ? `<img src="${p.image}" alt="${p.title}" />` : '<div class="chip-placeholder"></div>'}
                    </div>
                    <div class="chip-info">
                        <span class="chip-title">${p.title}</span>
                    </div>
                </div>
            `).join('');

            compareItemsCont.querySelectorAll('[data-remove-id]').forEach(btn => {
                btn.onclick = () => {
                    const removeId = btn.getAttribute('data-remove-id');
                    removeCompareProduct(removeId);
                };
            });
        }

        syncCompareCheckboxes();
    }

    function addCompareProduct(productData) {
        let stored = getStoredCompare();

        // If stored products are from a different category, auto-overwrite
        if (stored.length > 0 && productData.category) {
            const currentCat = stored[0].category;
            if (currentCat && currentCat !== productData.category) {
                stored = [];
            }
        }

        if (stored.length >= MAX_COMPARE) {
            alert(`You can compare up to ${MAX_COMPARE} products at a time.`); // eslint-disable-line no-alert
            syncCompareCheckboxes();
            return false;
        }

        if (!stored.some(p => String(p.id) === String(productData.id))) {
            stored.push(productData);
            setStoredCompare(stored);
        }

        updateCompareBar();
        return true;
    }

    if (compareClearBtn) {
        compareClearBtn.onclick = () => {
            setStoredCompare([]);
            updateCompareBar();
        };
    }

    // Global listener for compare checkboxes
    document.addEventListener('change', e => {
        const cb = e.target;
        if (!cb || !cb.matches('input[data-compare-checkbox]')) return;

        const pid = cb.getAttribute('data-product-id');
        const ptitle = cb.getAttribute('data-product-title') || '';
        const pcat = cb.getAttribute('data-product-category') || '';
        const pimg = cb.getAttribute('data-product-image') || '';
        const pslug = cb.getAttribute('data-product-slug') || cb.getAttribute('data-product-title') || '';

        if (cb.checked) {
            addCompareProduct({
                id: pid,
                title: ptitle,
                category: pcat,
                image: pimg,
                slug: pslug,
            });
        } else {
            removeCompareProduct(pid);
        }
    });

    document.addEventListener('listing:updated', () => {
        syncCompareCheckboxes();
    });

    updateCompareBar();
}
